import { inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ClientInvoiceDraftItem, CustomHttpResponse, DocumentSections } from '../../../models';
import { ApiHttpService } from '../../api-http.service';
import { SectionsFor } from '../../../types';

@Injectable({
  providedIn: 'root',
})
export class ClientInvoiceDraftItemService {
  apiService: ApiHttpService<ClientInvoiceDraftItem> = inject(ApiHttpService<ClientInvoiceDraftItem>);

  getForSupplierInvoice(iri: string): Observable<ClientInvoiceDraftItem[]> {
    return this.apiService
      .getMany(`${iri}/client_invoice_draft_items`)
      .pipe(map((result) => result.body['hydra:member']));
  }

  update(iri: string, clientInvoiceDraftItem: ClientInvoiceDraftItem): Observable<ClientInvoiceDraftItem> {
    return this.apiService
      .patch(iri, clientInvoiceDraftItem)
      .pipe(map((result: CustomHttpResponse<ClientInvoiceDraftItem>) => result.body));
  }

  delete(clientInvoiceDraftItem: ClientInvoiceDraftItem) {
    return this.apiService.delete(`${clientInvoiceDraftItem['@id']}`).pipe(map((result) => result));
  }

  createDraftFromSupplierInvoiceSection(
    supplierInvoiceSectionId: string,
    supplierInvoiceItemIri: string,
  ): Observable<ClientInvoiceDraftItem> {
    return this.apiService
      .post(`${supplierInvoiceSectionId}/add_supplier_invoice_item_to_draft`, {
        createdFromSupplierInvoiceItem: supplierInvoiceItemIri,
      } as unknown as ClientInvoiceDraftItem)
      .pipe(map((result: CustomHttpResponse<ClientInvoiceDraftItem>) => result.body as ClientInvoiceDraftItem));
  }

  createFromSupplierInvoiceSection(
    supplierInvoiceSectionIri: string,
    payload: ClientInvoiceDraftItem,
  ): Observable<ClientInvoiceDraftItem> {
    return this.apiService
      .post(`${supplierInvoiceSectionIri}/create_new_draft_with_items`, payload)
      .pipe(map((result: CustomHttpResponse<ClientInvoiceDraftItem>) => result.body as ClientInvoiceDraftItem));
  }

  getForSupplierInvoiceSection(iris: string[]): Observable<DocumentSections<ClientInvoiceDraftItem>> {
    const requests = iris.map((iri) =>
      this.apiService.getMany(`${iri}/client_invoice_draft_items`).pipe(
        map((res) => ({ iri, items: res.body['hydra:member'] as ClientInvoiceDraftItem[] })),
        // keep the key even if one call fails
        catchError(() => of({ iri, items: [] })),
      ),
    );

    return forkJoin(requests).pipe(
      map((pairs) => {
        // Start as a partial of the exact-keys type, then return it
        const acc: Partial<SectionsFor<readonly string[], ClientInvoiceDraftItem>> = {};
        for (const { iri, items } of pairs) acc[iri] = items;
        return acc as SectionsFor<typeof iris, ClientInvoiceDraftItem>;
      }),
    );
  }
}
